import { useEffect, useState } from 'react'
import { router } from '@inertiajs/react'
import { MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

interface Snapshot {
  configuration: { enabled: boolean; credential?: string; channels?: string[]; channel_mode?: 'selected' | 'invited'; preserved_credentials?: boolean }
  credentials: string[]; runtime: string; deliveries: Array<{ id: string; state: string }>; copy: Record<string, string>
}

export default function CodexSlackCard({ workerId, canManage }: { workerId: string; canManage: boolean }) {
  const [scopedSnapshot, setSnapshot] = useState<{ path: string; data: Snapshot } | null>(null)
  const [revision, setRevision] = useState(0)
  const [credential, setCredential] = useState('')
  const [channels, setChannels] = useState('')
  const [channelMode, setChannelMode] = useState<'selected' | 'invited'>('invited')
  const [busy, setBusy] = useState(false)
  const path = `/dashboard/digital_workers/${encodeURIComponent(workerId)}/codex_slack`
  const snapshot = scopedSnapshot?.path === path && canManage ? scopedSnapshot.data : null
  useEffect(() => {
    if (!canManage) return
    const controller = new AbortController()
    const read = async () => {
      try {
        const response = await fetch(path, { headers: { Accept: 'application/json' }, signal: controller.signal })
        if (!response.ok) { setSnapshot(null); return }
        const value = await response.json() as Snapshot
        if (!controller.signal.aborted) {
          setSnapshot({ path, data: value }); setCredential(value.configuration.credential ?? '')
          setChannels(value.configuration.channels?.join(', ') ?? '')
          setChannelMode(value.configuration.channel_mode ?? (value.configuration.enabled ? 'selected' : 'invited'))
        }
      } catch { /* Do not render stale settings from another worker. */ }
    }
    void read()
    return () => { controller.abort() }
  }, [path, canManage, revision])
  if (!snapshot) return null
  const copy = snapshot.copy, enabled = snapshot.configuration.enabled
  const preserved = snapshot.configuration.preserved_credentials === true
  const save = (activate: boolean) => {
    setBusy(true)
    router.patch(path, { slack: { enabled: activate, ...(credential ? { credential } : {}), channel_mode: channelMode, channels: channels.split(/[\s,]+/).filter(Boolean) } },
      { preserveScroll: true, onSuccess: () => setRevision(value => value + 1), onFinish: () => setBusy(false) })
  }
  return <section className="rounded-lg border border-border bg-card p-4">
    <div className="mb-2 flex items-center gap-2"><MessageSquare className="size-4 text-muted-foreground" /><h3 className="text-sm font-semibold">{copy.title}</h3>
      <span className="ml-auto text-xs text-muted-foreground">{enabled ? copy.enabled : copy.disabled}</span></div>
    <p className="mb-3 text-xs leading-5 text-muted-foreground">{copy.description}</p>
    <div className="space-y-2">
      <Select value={credential || (preserved ? "__preserved__" : "")} onValueChange={value => setCredential(value === "__preserved__" ? "" : value)}><SelectTrigger aria-label={copy.credential}><SelectValue placeholder={copy.credential} /></SelectTrigger>
        <SelectContent>{preserved && <SelectItem value="__preserved__">{copy.preserved_credential}</SelectItem>}{snapshot.credentials.map(name => <SelectItem key={name} value={name}>{name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={channelMode} onValueChange={value => setChannelMode(value as 'selected' | 'invited')}>
        <SelectTrigger aria-label={copy.channel_mode}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="invited">{copy.invited_channels}</SelectItem><SelectItem value="selected">{copy.selected_channels}</SelectItem></SelectContent>
      </Select>
      {channelMode === 'selected' && <Input value={channels} onChange={event => setChannels(event.target.value)} aria-label={copy.channels} placeholder={copy.channels} />}
      <p className="text-[11px] leading-4 text-muted-foreground">{copy.vault_hint}</p>
      {snapshot.deliveries.some(row => ['failed', 'delivery_unknown'].includes(row.state)) && <p role="status" className="text-xs text-destructive">{copy.delivery_warning}</p>}
      <div className="flex justify-end gap-2">
        {enabled && <Button size="sm" variant="ghost" disabled={busy} onClick={() => save(false)}>{copy.disconnect}</Button>}
        <Button size="sm" variant="outline" disabled={busy || (!credential && !preserved) || (channelMode === 'selected' && !channels.trim())} onClick={() => save(true)}>{enabled ? copy.save : copy.connect}</Button>
      </div>
    </div>
  </section>
}
